// src/pages/AccountingPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Field, Input, Select, Button, DangerButton, Textarea } from '../ui';
import { useCountry } from '../context/CountryContext';

const client = createAuditedClient('AccountingPage');

/* ================= GraphQL ================= */

const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items { id name currencies }
      nextToken
    }
  }
`;

const ListCars = /* GraphQL */ `
  query ListCars($filter: ModelCarFilterInput, $limit: Int) {
    listCars(filter: $filter, limit: $limit) {
      items { id name plate CompanyId active }
      nextToken
    }
  }
`;

const ListAccountingEntries = /* GraphQL */ `
  query ListAccountingEntries($filter: ModelAccountingEntryFilterInput, $limit: Int) {
    listAccountingEntries(filter: $filter, limit: $limit) {
      items {
        id
        CompanyId
        date
        type
        category
        description
        amount
        currency
        carId
        reservationId
        notes

        paymentMethod
        rentalIncludes

        submittedBySub
        submittedByEmail
        resolved
        resolvedAt
        resolvedBySub
        resolvedByEmail
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;


const CreateAccountingEntry = /* GraphQL */ `
  mutation CreateAccountingEntry($input: CreateAccountingEntryInput!) {
    createAccountingEntry(input: $input) {
      id
    }
  }
`;

const UpdateAccountingEntry = /* GraphQL */ `
  mutation UpdateAccountingEntry($input: UpdateAccountingEntryInput!) {
    updateAccountingEntry(input: $input) {
      id
      updatedAt
    }
  }
`;

const DeleteAccountingEntry = /* GraphQL */ `
  mutation DeleteAccountingEntry($input: DeleteAccountingEntryInput!) {
    deleteAccountingEntry(input: $input) {
      id
    }
  }
`;

/* ================= Helpers ================= */
const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'OTHER', label: 'Other' },
];

const RENTAL_INCLUDE_OPTIONS = [
  'Insurance',
  'Booster seat',
  'Infant seat',
  'Baby seat',
  'Snow chains',
];

function parseDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatDateDDMMYYYY(d) {
  const dt = typeof d === 'string' ? parseDate(d) : d;
  if (!dt) return '—';
  const day = String(dt.getDate()).padStart(2, '0');
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const year = String(dt.getFullYear());
  return `${day}/${month}/${year}`;
}

function formatDateTime(d) {
  const dt = typeof d === 'string' ? parseDate(d) : d;
  if (!dt) return '—';
  return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function formatDateForInput(d) {
  const dt = typeof d === 'string' ? parseDate(d) : d;
  if (!dt) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function money(n) {
  const x = safeNumber(n);
  return x.toFixed(2);
}
function moneyWithCurrency(amount, currency) {
  const cur = currency || '—';
  return `${money(amount)} ${cur}`;
}

function shortId(s, len = 8) {
  if (!s) return '—';
  return String(s).length <= len ? String(s) : `${String(s).slice(0, len)}…`;
}

function getSubmittedByDisplay(entry) {
  return entry?.submittedBySub || entry?.submittedByEmail || '—';
}

function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);

    if (m.addEventListener) m.addEventListener('change', onChange);
    else m.addListener(onChange);

    setMatches(m.matches);
    return () => {
      if (m.removeEventListener) m.removeEventListener('change', onChange);
      else m.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

/* ================= Main ================= */

export default function AccountingPage() {
  const [ready, setReady] = useState(false);
  const [claims, setClaims] = useState(null);
  const [groups, setGroups] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [cars, setCars] = useState([]);
  const [entries, setEntries] = useState([]);

  const { selectedCompanyId: ctxCompanyId } = useCountry();

  const isAdmin = useMemo(() => groups.includes('admin'), [groups]);
  const isCountryAdmin = useMemo(() => groups.includes('country_admin'), [groups]);
  const isEditor = useMemo(() => groups.includes('editor'), [groups]);
  const canEdit = isAdmin || isCountryAdmin || isEditor;

  const roleLabel = useMemo(() => {
    if (isAdmin) return 'Admin';
    if (isCountryAdmin) return 'Country admin';
    if (isEditor) return 'Editor';
    return 'View only';
  }, [isAdmin, isCountryAdmin, isEditor]);

  const myCompanyId = claims?.CompanyId || null;
  const activeCompanyId = useMemo(
    () => (isAdmin ? (ctxCompanyId || myCompanyId || null) : myCompanyId || null),
    [isAdmin, ctxCompanyId, myCompanyId]
  );

  const selectedCompany = useMemo(
    () => companies.find(c => c.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const currencyOptions = useMemo(() => {
    const list = selectedCompany?.currencies || [];
    return list.filter(Boolean);
  }, [selectedCompany]);

  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // filters
  const today = new Date();
  const [filterYear, setFilterYear] = useState(today.getFullYear());
  const [filterMonth, setFilterMonth] = useState(today.getMonth() + 1); // 1-12
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [resolvedFilter, setResolvedFilter] = useState('ALL'); // ALL | OPEN | RESOLVED

  // collapsible sections
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [incomeOpen, setIncomeOpen] = useState(true);
  const [expenseOpen, setExpenseOpen] = useState(true);

  useEffect(() => {
    if (isMobile) setFiltersOpen(false);
    else setFiltersOpen(true);
  }, [isMobile]);

  // form state
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState({
    date: formatDateForInput(today),
    type: 'INCOME',
    category: '',
    description: '',
    amount: '',
    currency: '',
    carId: '',

    paymentMethod: 'CASH',
    isRentalIncome: false,
    rentalIncludes: [],

    submittedByName: '',
  });


  /* ===== Toast + Confirm ===== */
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  const [confirmState, setConfirmState] = useState(null);
  const confirmResolveRef = useRef(null);

  function showAlert(message, variant = 'info') {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, variant });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3500);
  }

  function hideAlert() {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }

  function openConfirm(options) {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        title: options.title || 'Confirm action',
        message: options.message || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
      });
    });
  }

  function handleConfirm(result) {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(result);
      confirmResolveRef.current = null;
    }
    setConfirmState(null);
  }

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  /* -------- auth -------- */
  useEffect(() => {
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const p = tokens?.idToken?.payload || {};
        const g = p['cognito:groups'] || [];
        setGroups(g);
        setClaims({
          CompanyId: p['custom:CompanyId'] ?? p['custom:companyId'] ?? null,
          sub: p.sub || null,
          email: p.email || null,
        });
      } catch (e) {
        console.error('AccountingPage auth failed', e);
        setGroups([]);
        setClaims(null);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  /* -------- load companies -------- */
  useEffect(() => {
    if (!ready) return;
    (async () => {
      try {
        const res = await client.graphql({ query: ListCompanies, authMode: 'userPool' });
        const items = (res.data?.listCompanies?.items || []).filter(Boolean);
        setCompanies(items);
      } catch (e) {
        console.error('Load companies failed', e);
        setCompanies([]);
        showAlert('Failed to load agencies. Check console/logs.', 'error');
      }
    })();
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    if (!activeCompanyId) {
      setSelectedCompanyId(null);
      return;
    }
    setSelectedCompanyId(prev => (prev === activeCompanyId ? prev : activeCompanyId));
  }, [ready, activeCompanyId]);

  async function reloadLedger(companyId) {
    const res = await client.graphql({
      query: ListAccountingEntries,
      variables: { filter: { CompanyId: { eq: companyId } }, limit: 1000 },
      authMode: 'userPool',
    });
    const items = (res.data?.listAccountingEntries?.items || []).filter(Boolean);
    items.sort((a, b) => {
      const da = parseDate(a.date)?.getTime() || 0;
      const db = parseDate(b.date)?.getTime() || 0;
      if (da !== db) return db - da;
      const ca = parseDate(a.createdAt)?.getTime() || 0;
      const cb = parseDate(b.createdAt)?.getTime() || 0;
      return cb - ca;
    });
    setEntries(items);
  }

  /* -------- load cars + entries for selected company -------- */
  useEffect(() => {
    if (!selectedCompanyId || !ready) return;

    (async () => {
      setLoading(true);
      try {
        const [carRes] = await Promise.all([
          client.graphql({
            query: ListCars,
            variables: { filter: { CompanyId: { eq: selectedCompanyId } }, limit: 1000 },
            authMode: 'userPool',
          }),
          reloadLedger(selectedCompanyId),
        ]);

        const carItems = (carRes.data?.listCars?.items || []).filter(Boolean);
        setCars(carItems);
      } catch (e) {
        console.error('Load accounting data failed', e);
        setCars([]);
        setEntries([]);
        showAlert('Failed to load accounting data. Check schema + console logs.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedCompanyId, ready]);

  /* -------- derived: carName map -------- */
  const carMap = useMemo(() => {
    const m = new Map();
    for (const c of cars) {
      m.set(c.id, `${c.name}${c.plate ? ` (${c.plate})` : ''}`);
    }
    return m;
  }, [cars]);

  /* -------- derived: filters -------- */
  const monthEntries = useMemo(() => {
    if (!entries.length) return [];
    return entries.filter(e => {
      const dt = parseDate(e.date);
      if (!dt) return false;
      const y = dt.getFullYear();
      const m = dt.getMonth() + 1;
      return y === Number(filterYear) && m === Number(filterMonth);
    });
  }, [entries, filterYear, filterMonth]);

  const filteredEntries = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    const cat = (categoryFilter || '').trim().toLowerCase();

    return monthEntries.filter(e => {
      const isResolved = !!e.resolved;
      if (resolvedFilter === 'OPEN' && isResolved) return false;
      if (resolvedFilter === 'RESOLVED' && !isResolved) return false;

      if (cat && String(e.category || '').toLowerCase() !== cat) return false;

      if (!q) return true;
      const hay = [
        e.type,
        e.category,
        e.description,
        e.currency,
        e.reservationId, // optional: you can remove this too if you want fully gone
        e.paymentMethod,
        Array.isArray(e.rentalIncludes) ? e.rentalIncludes.join(' ') : '',
        e.carId ? carMap.get(e.carId) || e.carId : '',
        e.submittedBySub,
        e.submittedByEmail,
      ]

        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [monthEntries, search, categoryFilter, resolvedFilter, carMap]);

  const incomeEntries = useMemo(() => filteredEntries.filter(e => e.type === 'INCOME'), [filteredEntries]);
  const expenseEntries = useMemo(() => filteredEntries.filter(e => e.type === 'EXPENSE'), [filteredEntries]);

  const uniqueCategories = useMemo(() => {
    const set = new Set();
    for (const e of monthEntries) {
      if (e.category) set.add(String(e.category).trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [monthEntries]);

  const totals = useMemo(() => {
    const map = new Map(); // currency -> { income, expense }
    for (const e of filteredEntries) {
      const cur = e.currency || 'N/A';
      const amt = safeNumber(e.amount);
      if (!map.has(cur)) map.set(cur, { income: 0, expense: 0 });
      const row = map.get(cur);
      if (e.type === 'INCOME') row.income += amt;
      if (e.type === 'EXPENSE') row.expense += amt;
    }
    const rows = Array.from(map.entries()).map(([currency, v]) => ({
      currency,
      income: v.income,
      expense: v.expense,
      net: v.income - v.expense,
    }));
    rows.sort((a, b) => a.currency.localeCompare(b.currency));
    return rows;
  }, [filteredEntries]);

  const openCount = useMemo(() => filteredEntries.filter(e => !e.resolved).length, [filteredEntries]);
  const resolvedCount = useMemo(() => filteredEntries.filter(e => !!e.resolved).length, [filteredEntries]);

  /* -------- form helpers -------- */

  function resetForm() {
    setEditingEntry(null);
    setForm({
      date: formatDateForInput(today),
      type: 'INCOME',
      category: '',
      description: '',
      amount: '',
      currency: currencyOptions[0] || '',
      carId: '',

      paymentMethod: 'CASH',
      isRentalIncome: false,
      rentalIncludes: [],

      submittedByName: '',
    });
  }


  function startEdit(entry) {
    setEditingEntry(entry);
    setForm({
      date: formatDateForInput(entry.date),
      type: entry.type || 'INCOME',
      category: entry.category || '',
      description: entry.description || '',
      amount: entry.amount != null ? String(entry.amount) : '',
      currency: entry.currency || '',
      carId: entry.carId || '',

      paymentMethod: entry.paymentMethod || 'CASH',
      isRentalIncome: Array.isArray(entry.rentalIncludes) && entry.rentalIncludes.length > 0,
      rentalIncludes: Array.isArray(entry.rentalIncludes) ? entry.rentalIncludes : [],
      submittedByName: entry.submittedBySub || entry.submittedByEmail || '',
    });

    if (entry.type === 'INCOME') setIncomeOpen(true);
    if (entry.type === 'EXPENSE') setExpenseOpen(true);
  }


  async function handleSubmit(e) {
    e.preventDefault();
    if (!canEdit || !selectedCompanyId) return;

    if (!form.submittedByName?.trim()) {
      showAlert('Set “Submitted by (name)”.', 'error');
      return;
    }
    if (!form.currency) {
      showAlert('Pick a currency.', 'error');
      return;
    }
    if (!form.amount || !Number.isFinite(Number(form.amount))) {
      showAlert('Enter a valid amount.', 'error');
      return;
    }
    if (!form.description?.trim() && !form.category?.trim()) {
      showAlert('At least set a category or a description.', 'error');
      return;
    }
    if (!form.paymentMethod) {
      showAlert('Pick a payment method.', 'error');
      return;
    }

    const base = {
      CompanyId: selectedCompanyId,
      date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
      type: form.type,
      category: form.category || null,
      description: form.description || null,
      amount: safeNumber(form.amount),
      currency: form.currency || null,
      carId: form.carId || null,

      // removed from UI: keep null so you don't carry garbage
      reservationId: null,
      notes: null,

      paymentMethod: form.paymentMethod || null,
      rentalIncludes: form.type === 'INCOME' && form.isRentalIncome ? (form.rentalIncludes || []) : [],

      submittedBySub: form.submittedByName.trim(),
      submittedByEmail: null,
    };


    try {
      if (editingEntry) {
        await client.graphql({
          query: UpdateAccountingEntry,
          variables: { input: { id: editingEntry.id, ...base } },
          authMode: 'userPool',
        });
      } else {
        await client.graphql({
          query: CreateAccountingEntry,
          variables: {
            input: {
              ...base,
              resolved: false,
              resolvedAt: null,
              resolvedBySub: null,
              resolvedByEmail: null,
            },
          },
          authMode: 'userPool',
        });
      }

      await reloadLedger(selectedCompanyId);
      resetForm();
      showAlert(editingEntry ? 'Entry updated.' : 'Entry added.', 'success');
    } catch (err) {
      console.error('Save accounting entry failed', err);
      showAlert('Failed to save entry. Check schema + console logs.', 'error');
    }
  }

  async function handleDelete(entry) {
    if (!canEdit) return;

    const confirmed = await openConfirm({
      title: 'Delete accounting entry',
      message: 'Are you sure you want to delete this entry?',
      confirmLabel: 'Delete entry',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await client.graphql({
        query: DeleteAccountingEntry,
        variables: { input: { id: entry.id } },
        authMode: 'userPool',
      });

      setEntries(prev => prev.filter(e => e.id !== entry.id));
      if (editingEntry && editingEntry.id === entry.id) resetForm();
      showAlert('Entry deleted.', 'success');
    } catch (err) {
      console.error('Delete accounting entry failed', err);
      showAlert('Failed to delete entry. Check console/logs.', 'error');
    }
  }

  async function toggleResolved(entry, next) {
    if (!isAdmin) {
      showAlert('Only admins can resolve entries.', 'error');
      return;
    }

    setEntries(prev =>
      prev.map(e =>
        e.id === entry.id
          ? {
            ...e,
            resolved: next,
            resolvedAt: next ? new Date().toISOString() : null,
            resolvedBySub: next ? (claims?.sub || null) : null,
            resolvedByEmail: next ? (claims?.email || null) : null,
          }
          : e
      )
    );

    try {
      await client.graphql({
        query: UpdateAccountingEntry,
        variables: {
          input: {
            id: entry.id,
            resolved: next,
            resolvedAt: next ? new Date().toISOString() : null,
            resolvedBySub: next ? (claims?.sub || null) : null,
            resolvedByEmail: next ? (claims?.email || null) : null,
          },
        },
        authMode: 'userPool',
      });
      showAlert(next ? 'Marked resolved.' : 'Marked open.', 'success');
    } catch (err) {
      console.error('Toggle resolved failed', err);
      showAlert('Failed to update resolved state. Check schema + redeploy if needed.', 'error');
      if (selectedCompanyId) {
        try {
          await reloadLedger(selectedCompanyId);
        } catch (_) { }
      }
    }
  }

  /* -------- render -------- */

  if (!ready) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>Loading…</div>
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
        {confirmState && <ConfirmDialog state={confirmState} onResult={handleConfirm} />}
      </div>
    );
  }

  if (!claims) {
    return (
      <div style={{ padding: 24 }}>
        <b>Not authenticated.</b>
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
        {confirmState && <ConfirmDialog state={confirmState} onResult={handleConfirm} />}
      </div>
    );
  }

  return (
    <div style={{ background: '#f3f4f6', minHeight: 'calc(100vh - 64px)', overflowX: 'hidden' }}>
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '12px 10px 24px' : '16px 12px 32px',
          display: 'grid',
          gap: 16,
          minWidth: 0,
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 22 }}>Accounting</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              Separate income/expenses, track totals, and resolve entries properly.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
            <span style={pill(roleLabel, canEdit ? 'ok' : 'neutral')}>{roleLabel}</span>
            <span style={pill(`${openCount} open`, openCount ? 'warn' : 'neutral')}>{openCount} open</span>
            <span style={pill(`${resolvedCount} resolved`, resolvedCount ? 'ok' : 'neutral')}>
              {resolvedCount} resolved
            </span>
          </div>
        </header>

        {/* Agency + period + filters */}
        <section style={cardStyle()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: !isMobile || filtersOpen ? 10 : 0,
              cursor: isMobile ? 'pointer' : 'default',
              userSelect: 'none',
            }}
            onClick={() => {
              if (!isMobile) return;
              setFiltersOpen(v => !v);
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h4 style={{ margin: 0, fontSize: 16 }}>Agency, Period & Filters</h4>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
                Filter by month/year, category, text search, and resolved status.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {loading && <span style={{ fontSize: 12, color: '#6b7280' }}>Loading…</span>}
              {isMobile && (
                <span style={{ fontSize: 12, fontWeight: 900, color: '#111827' }}>
                  {filtersOpen ? '▾ Hide' : '▸ Show'}
                </span>
              )}
            </div>
          </div>

          {(!isMobile || filtersOpen) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                alignItems: 'flex-end',
                minWidth: 0,
              }}
            >
              <Field label="Agency">
                <Select
                  value={selectedCompanyId || ''}
                  onChange={e => setSelectedCompanyId(e.target.value || null)}
                  disabled={!isAdmin}
                >
                  {isAdmin ? (
                    <>
                      <option value="">Select agency…</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </>
                  ) : (
                    companies
                      .filter(c => c.id === selectedCompanyId)
                      .map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))
                  )}
                </Select>
              </Field>

              <Field label="Month">
                <Select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>
                      {m.toString().padStart(2, '0')}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Year">
                <Input
                  type="number"
                  value={filterYear}
                  onChange={e => setFilterYear(Number(e.target.value) || today.getFullYear())}
                />
              </Field>

              <Field label="Resolved">
                <Select value={resolvedFilter} onChange={e => setResolvedFilter(e.target.value)}>
                  <option value="ALL">All</option>
                  <option value="OPEN">Open only</option>
                  <option value="RESOLVED">Resolved only</option>
                </Select>
              </Field>

              <Field label="Category">
                <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                  <option value="">All categories</option>
                  {uniqueCategories.map(c => (
                    <option key={c} value={c.toLowerCase()}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Search">
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={isMobile ? 'Search…' : 'description, notes, ref, car, submitter…'}
                />
              </Field>
            </div>
          )}
        </section>

        {/* Totals */}
        <section style={cardStyle()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <h4 style={{ margin: 0, fontSize: 16 }}>Totals</h4>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
                Per currency: income, expenses, and net result for current filters.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={pill(`${filteredEntries.length} entries`, 'neutral')}>{filteredEntries.length} entries</span>
            </div>
          </div>

          {totals.length === 0 ? (
            <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>No entries.</div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 10,
                marginTop: 12,
                minWidth: 0,
              }}
            >
              {totals.map(row => (
                <div
                  key={row.currency}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 12,
                    background: '#f9fafb',
                    minWidth: 0,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ fontWeight: 900, fontSize: 13 }}>{row.currency}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      Net:{' '}
                      <span style={{ fontWeight: 900, color: row.net >= 0 ? '#166534' : '#b91c1c' }}>
                        {money(row.net)}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginTop: 8, display: 'grid', gap: 6, fontSize: 12 }}>
                    <div style={kvRow}>
                      <span style={muted}>Income</span>
                      <span style={{ fontWeight: 700, color: '#166534' }}>{money(row.income)}</span>
                    </div>
                    <div style={kvRow}>
                      <span style={muted}>Expenses</span>
                      <span style={{ fontWeight: 700, color: '#b91c1c' }}>{money(row.expense)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Form */}
        {canEdit && (
          <section style={cardStyle()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <h4 style={{ margin: 0, fontSize: 16 }}>{editingEntry ? 'Edit entry' : 'New entry'}</h4>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
                  Keep it clean: proper currency, meaningful category/description, and optional car/ref.
                </p>
              </div>
              {editingEntry && (
                <span style={pill(`Editing ${shortId(editingEntry.id, 10)}`, 'warn')}>
                  Editing {shortId(editingEntry.id, 10)}
                </span>
              )}
            </div>

            <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 12,
                  minWidth: 0,
                }}
              >
                <Field label="Submitted by (name)">
                  <Input
                    value={form.submittedByName}
                    onChange={e => setForm(f => ({ ...f, submittedByName: e.target.value }))}
                  />
                </Field>

                <Field label="Date">
                  <Input
                    type="date"
                    value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  />
                </Field>

                <Field label="Type">
                  <Select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="INCOME">Income</option>
                    <option value="EXPENSE">Expense</option>
                  </Select>
                </Field>

                <Field label="Currency">
                  <Select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                    <option value="">Select</option>
                    {currencyOptions.map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="Amount">
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  />
                </Field>

                <Field label="Category">
                  <Input
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    placeholder="e.g. Rental, Fuel, Repairs"
                  />
                </Field>

                <Field label="Car (optional)">
                  <Select value={form.carId} onChange={e => setForm(f => ({ ...f, carId: e.target.value }))}>
                    <option value="">None</option>
                    {cars.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.plate ? ` (${c.plate})` : ''}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="Payment method">
                  <Select
                    value={form.paymentMethod}
                    onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}
                  >
                    {PAYMENT_METHODS.map(m => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Rental income?">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={!!form.isRentalIncome}
                      onChange={e =>
                        setForm(f => ({
                          ...f,
                          isRentalIncome: e.target.checked,
                          rentalIncludes: e.target.checked ? (f.rentalIncludes || []) : [],
                        }))
                      }
                    />
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>Enable rental includes</span>
                  </label>
                </Field>

              </div>
              {form.type === 'INCOME' && form.isRentalIncome && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, color: '#111827' }}>
                    Rental includes
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                    {RENTAL_INCLUDE_OPTIONS.map(opt => {
                      const checked = (form.rentalIncludes || []).includes(opt);
                      return (
                        <label
                          key={opt}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 10px',
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            background: checked ? '#f0fdf4' : '#fff',
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={ev => {
                              const next = ev.target.checked;
                              setForm(f => {
                                const prev = Array.isArray(f.rentalIncludes) ? f.rentalIncludes : [];
                                const set = new Set(prev);
                                if (next) set.add(opt);
                                else set.delete(opt);
                                return { ...f, rentalIncludes: Array.from(set) };
                              });
                            }}
                          />
                          {opt}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginTop: 12, minWidth: 0 }}>
                <Field label="Description">
                  <Textarea
                    rows={3}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  />
                </Field>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <Button type="submit">{editingEntry ? 'Save changes' : 'Add entry'}</Button>
                {editingEntry && (
                  <Button type="button" style={{ background: 'white' }} onClick={resetForm}>
                    Cancel edit
                  </Button>
                )}
              </div>
            </form>
          </section>
        )}

        {/* Ledgers (VERTICAL + CLICKABLE HEADER/TAB) */}
        <section style={cardStyle()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <h4 style={{ margin: 0, fontSize: 16 }}>Ledgers</h4>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
                Income and expenses are separated. Admins can resolve entries.
              </p>
            </div>
            {!canEdit && <span style={{ fontSize: 11, color: '#9ca3af' }}>View only.</span>}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 12,
              marginTop: 12,
              minWidth: 0,
            }}
          >
            <LedgerBlock
              title="Income"
              subtitle="Money coming in"
              tone="income"
              entries={incomeEntries}
              carMap={carMap}
              canEdit={canEdit}
              isAdmin={isAdmin}
              onEdit={startEdit}
              onDelete={handleDelete}
              onToggleResolved={toggleResolved}
              compact={isMobile}
              collapsible={true}
              open={incomeOpen}
              onToggleOpen={() => setIncomeOpen(v => !v)}
            />

            <LedgerBlock
              title="Expenses"
              subtitle="Money going out"
              tone="expense"
              entries={expenseEntries}
              carMap={carMap}
              canEdit={canEdit}
              isAdmin={isAdmin}
              onEdit={startEdit}
              onDelete={handleDelete}
              onToggleResolved={toggleResolved}
              compact={isMobile}
              collapsible={true}
              open={expenseOpen}
              onToggleOpen={() => setExpenseOpen(v => !v)}
            />
          </div>
        </section>
      </div>

      {toast && <ToastBox toast={toast} onClose={hideAlert} />}
      {confirmState && <ConfirmDialog state={confirmState} onResult={handleConfirm} />}
    </div>
  );
}

/* ================= Ledger block ================= */

function LedgerBlock({
  title,
  subtitle,
  tone,
  entries,
  carMap,
  canEdit,
  isAdmin,
  onEdit,
  onDelete,
  onToggleResolved,
  compact = false,
  collapsible = false,
  open = true,
  onToggleOpen,
}) {
  const accent = tone === 'income' ? '#166534' : '#b91c1c';
  const bg = tone === 'income' ? '#ecfdf5' : '#fef2f2';

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', overflow: 'hidden', minWidth: 0 }}>
      {/* CLICKABLE ENTIRE HEADER AREA */}
      <div
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        style={{
          padding: 12,
          borderBottom: open ? '1px solid #e5e7eb' : 'none',
          background: bg,
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
        onClick={() => {
          if (!collapsible) return;
          if (typeof onToggleOpen === 'function') onToggleOpen();
        }}
        onKeyDown={e => {
          if (!collapsible) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (typeof onToggleOpen === 'function') onToggleOpen();
          }
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{title}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{subtitle}</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 900, color: accent }}>{entries.length} entries</span>
          {collapsible && (
            <span style={{ fontSize: 12, fontWeight: 900, color: '#111827' }}>{open ? '▾' : '▸'}</span>
          )}
        </div>
      </div>

      {!open ? null : entries.length === 0 ? (
        <div style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>No entries.</div>
      ) : compact ? (
        <div style={{ padding: 12, display: 'grid', gap: 10, minWidth: 0 }}>
          {entries.map(e => {
            const isResolved = !!e.resolved;

            return (
              <div
                key={e.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 12,
                  background: isResolved ? '#f0fdf4' : '#ffffff',
                  minWidth: 0,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{formatDateDDMMYYYY(e.date)}</div>
                    <div style={{ fontWeight: 900, fontSize: 13, color: '#111827', minWidth: 0 }}>
                      <span style={{ overflowWrap: 'anywhere' }}>{e.category || '—'}</span>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: '#111827',
                        lineHeight: 1.35,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {e.description || '—'}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 900, fontSize: 16, color: accent }}>
                      {moneyWithCurrency(e.amount, e.currency)}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, overflowWrap: 'anywhere' }}>
                      {e.reservationId ? `Ref: ${shortId(e.reservationId, 12)}` : 'Ref: —'}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 10, display: 'grid', gap: 6, fontSize: 12, minWidth: 0 }}>
                  <div style={kvRow}>
                    <span style={muted}>Car</span>
                    <span style={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {e.carId ? carMap.get(e.carId) || e.carId : '—'}
                    </span>
                  </div>

                  <div style={kvRow}>
                    <span style={muted}>Submitted</span>
                    <span style={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {getSubmittedByDisplay(e)}
                    </span>
                  </div>
                  {Array.isArray(e.rentalIncludes) && e.rentalIncludes.length > 0 && (
                    <div style={kvRow}>
                      <span style={muted}>Rental includes</span>
                      <span style={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere', textAlign: 'right' }}>
                        {e.rentalIncludes.join(', ')}
                      </span>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: isAdmin ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={ev => ev.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={!!e.resolved}
                        disabled={!isAdmin}
                        onChange={ev => onToggleResolved(e, ev.target.checked)}
                      />
                      <span style={{ fontSize: 12, fontWeight: 900, color: e.resolved ? '#166534' : '#6b7280' }}>
                        {e.resolved ? 'Resolved' : 'Open'}
                      </span>
                    </label>

                    {canEdit && (
                      <div style={{ display: 'flex', gap: 8 }} onClick={ev => ev.stopPropagation()}>
                        <Button
                          type="button"
                          style={{ background: 'white', padding: '6px 10px', fontSize: 12 }}
                          onClick={() => onEdit(e)}
                        >
                          Edit
                        </Button>
                        <DangerButton
                          type="button"
                          style={{ padding: '6px 10px', fontSize: 12 }}
                          onClick={() => onDelete(e)}
                        >
                          Delete
                        </DangerButton>
                      </div>
                    )}
                  </div>

                  {e.resolved && (e.resolvedAt || e.resolvedByEmail || e.resolvedBySub) && (
                    <div style={{ fontSize: 11, color: '#6b7280', overflowWrap: 'anywhere' }}>
                      {e.resolvedAt ? formatDateTime(e.resolvedAt) : ''}
                      {e.resolvedByEmail
                        ? ` • ${e.resolvedByEmail}`
                        : e.resolvedBySub
                          ? ` • ${shortId(e.resolvedBySub, 10)}`
                          : ''}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ maxHeight: 560, overflow: 'auto' }}>
          <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Category</th>
                <th style={th}>Description</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                <th style={th}>Car</th>
                <th style={th}>Method</th>
                <th style={th}>Includes</th>
                <th style={th}>Submitted by</th>
                <th style={th}>Resolved</th>
                {canEdit && <th style={{ ...th, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, idx) => {
                const isResolved = !!e.resolved;
                const rowBg = isResolved
                  ? idx % 2 === 0
                    ? '#f0fdf4'
                    : '#dcfce7'
                  : idx % 2 === 0
                    ? '#ffffff'
                    : '#f9fafb';

                return (
                  <tr
                    key={e.id}
                    style={{
                      borderTop: '1px solid #f3f4f6',
                      background: rowBg,
                      opacity: isResolved ? 0.92 : 1,
                    }}
                  >
                    <td style={td}>{formatDateDDMMYYYY(e.date)}</td>
                    <td style={td}>{e.category || '—'}</td>
                    <td style={td}>{e.description || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: accent }}>
                      {moneyWithCurrency(e.amount, e.currency)}
                    </td>
                    <td style={td}>{e.carId ? carMap.get(e.carId) || e.carId : '—'}</td>
                    <td style={td}>{e.paymentMethod || '—'}</td>
                    <td style={td}>
                      {Array.isArray(e.rentalIncludes) && e.rentalIncludes.length > 0 ? e.rentalIncludes.join(', ') : '—'}
                    </td>

                    <td style={td}>
                      <div style={{ display: 'grid', gap: 2 }}>
                        <span style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{getSubmittedByDisplay(e)}</span>
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'grid', gap: 3 }}>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: isAdmin ? 'pointer' : 'default',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!e.resolved}
                            disabled={!isAdmin}
                            onChange={ev => onToggleResolved(e, ev.target.checked)}
                          />
                          <span style={{ fontSize: 12, fontWeight: 800, color: e.resolved ? '#166534' : '#6b7280' }}>
                            {e.resolved ? 'Resolved' : 'Open'}
                          </span>
                        </label>

                        {e.resolved && (e.resolvedAt || e.resolvedByEmail || e.resolvedBySub) && (
                          <div style={{ fontSize: 11, color: '#6b7280' }}>
                            {e.resolvedAt ? formatDateTime(e.resolvedAt) : ''}
                            {e.resolvedByEmail
                              ? ` • ${e.resolvedByEmail}`
                              : e.resolvedBySub
                                ? ` • ${shortId(e.resolvedBySub, 10)}`
                                : ''}
                          </div>
                        )}
                      </div>
                    </td>

                    {canEdit && (
                      <td style={{ ...td, textAlign: 'right', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                          <Button
                            type="button"
                            style={{ background: 'white', padding: '3px 8px', fontSize: 11, minWidth: 78 }}
                            onClick={() => onEdit(e)}
                          >
                            Edit
                          </Button>
                          <DangerButton
                            type="button"
                            style={{ padding: '3px 8px', fontSize: 11, minWidth: 78 }}
                            onClick={() => onDelete(e)}
                          >
                            Delete
                          </DangerButton>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================= Toast / Confirm components ================= */

function ToastBox({ toast, onClose }) {
  return (
    <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 9999, maxWidth: 360 }}>
      <div
        style={{
          borderRadius: 12,
          padding: '10px 12px',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          boxShadow: '0 10px 25px rgba(15,23,42,0.18)',
          border: '1px solid rgba(148,163,184,0.5)',
          background: toast.variant === 'success' ? '#e7f9ec' : toast.variant === 'error' ? '#fef2f2' : '#f9fafb',
          color: toast.variant === 'success' ? '#166534' : toast.variant === 'error' ? '#991b1b' : '#111827',
          fontSize: 13,
        }}
      >
        <div style={{ fontSize: 16 }}>{toast.variant === 'success' ? '✓' : toast.variant === 'error' ? '!' : 'ℹ'}</div>
        <div style={{ flex: 1, overflowWrap: 'anywhere' }}>{toast.message}</div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            margin: 0,
            fontSize: 14,
            lineHeight: 1,
            color: 'inherit',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({ state, onResult }) {
  if (!state) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.35)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
      }}
      onClick={() => onResult(false)}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: 14,
          padding: 16,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 20px 40px rgba(15,23,42,0.35)',
          border: '1px solid #e5e7eb',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h4 style={{ margin: '0 0 6px', fontSize: 16 }}>{state.title}</h4>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#4b5563' }}>{state.message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={() => onResult(false)} style={{ background: 'white' }}>
            {state.cancelLabel}
          </Button>
          <Button onClick={() => onResult(true)} style={{ background: '#dc2626' }}>
            {state.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ================= Styles ================= */

const th = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '1px solid #e5e7eb',
  fontWeight: 800,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#6b7280',
  background: '#f9fafb',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const td = {
  padding: '8px 10px',
  verticalAlign: 'middle',
  fontSize: 12,
  color: '#111827',
};

function cardStyle() {
  return {
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 14,
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    minWidth: 0,
  };
}

function pill(text, tone) {
  const base = {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '5px 10px',
    borderRadius: 999,
    border: '1px solid #d1d5db',
    background: '#f3f4f6',
    color: '#374151',
    whiteSpace: 'nowrap',
    fontWeight: 900,
  };

  if (tone === 'ok') return { ...base, background: '#e7f9ec', color: '#166534', border: '1px solid #bbf7d0' };
  if (tone === 'warn') return { ...base, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' };
  return base;
}

const muted = { color: '#6b7280' };

const kvRow = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
};
